import { ITEM_TYPES, ItemType, TabKeys } from "../types";

/**
 * Single source of truth mapping a canvas item type to the settings tab that
 * edits it. Mirrors the `setActiveTab` calls scattered across the Template
 * preview components so selecting an item from the layers list opens the same
 * tab a canvas click does.
 */
export const getTabForItemType = (
  itemType: ItemType["type"]
): TabKeys | undefined => {
  switch (itemType) {
    case ITEM_TYPES.Image:
    case ITEM_TYPES.Video:
    case ITEM_TYPES.Background:
    case ITEM_TYPES.Shape:
      return TabKeys.objects;
    case ITEM_TYPES.CustomText:
    case ITEM_TYPES.ParameteredText:
    case ITEM_TYPES.Subtitles:
    case ITEM_TYPES.Title:
    case ITEM_TYPES.Table:
      return TabKeys.content;
    case ITEM_TYPES.Presenter:
      return TabKeys.avatar;
    case ITEM_TYPES.Logo:
      return TabKeys.brand;
    default:
      return undefined;
  }
};

/**
 * Whether the given tab is the one StoryboardMenu treats as "owning" the given
 * item type, used to decide if a tab change should clear the current selection.
 *
 * This is intentionally narrower than {@link getTabForItemType}: only the
 * directly-editable canvas items (text, media, shape, presenter, logo) match a
 * tab here. Structural/auxiliary items (Background, ParameteredText) do not,
 * preserving the menu's pre-existing clear-on-tab-change behavior.
 */
export const tabMatchesItem = (
  tab: TabKeys,
  itemType: ItemType["type"] | undefined
): boolean => {
  if (!itemType) return false;
  switch (tab) {
    case TabKeys.content:
      return (
        itemType === ITEM_TYPES.Subtitles ||
        itemType === ITEM_TYPES.Title ||
        itemType === ITEM_TYPES.CustomText ||
        itemType === ITEM_TYPES.Table
      );
    case TabKeys.objects:
      return (
        itemType === ITEM_TYPES.Image ||
        itemType === ITEM_TYPES.Video ||
        itemType === ITEM_TYPES.Shape
      );
    case TabKeys.brand:
      return itemType === ITEM_TYPES.Logo;
    case TabKeys.avatar:
      return itemType === ITEM_TYPES.Presenter;
    default:
      return false;
  }
};
